import { createRef, memo, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import styles from './Checkbox.module.scss';

const Checkbox = ({ checked, indeterminate, disabled, onChange, label, name, ...props }) => {
    const [isChecked, setIsChecked] = useState(Boolean(checked));
    const [isIndeterminate, setIsIndeterminate] = useState(indeterminate);
    const cbRef = createRef();
    const inputRef = createRef();

    useEffect(() => {
        setIsChecked(Boolean(checked));
    }, [checked]);

    const handleClick = () => {
        if (!disabled) {
            const checked = !isChecked;
            onChange({ name: name || label, checked, indeterminate, disabled });
            setIsChecked(checked);
            if (cbRef?.current) {
                cbRef.current.focus({
                    preventScroll: true
                });
            }
        }
    };

    const handleKeypress = evt => {
        const { code, key, keyCode } = evt;
        if (keyCode === 13 || key === 'Enter' || code === 'Space' || keyCode === 32) {
            handleClick();
        }
    };

    useEffect(() => {
        if (inputRef?.current) {
            inputRef.current.indeterminate = indeterminate;
            setIsIndeterminate(indeterminate);
        }
    }, [indeterminate]);

    return (
        <div className={styles.container} data-testid={props['data-testid']}>
            <div className={styles.border} onClick={handleClick}>
                <div
                    ref={cbRef}
                    className={[styles.box, disabled ? styles.disabled : null].join(' ')}
                    tabIndex={disabled ? -1 : 0}
                    onKeyDown={handleKeypress}
                    data-testid={props['data-testid'] ? props['data-testid'] + '.checkbox' : null}
                    aria-checked={indeterminate ? 'mixed' : Boolean(isChecked)}
                    aria-disabled={Boolean(disabled)}
                    role="checkbox"
                    aria-labelledby={name}
                    title={label}
                >
                    {isChecked && <div className={[isIndeterminate ? styles.indeterminate : styles.tick, disabled ? styles.disabled : null].join(' ')}/>}
                </div>
            </div>

            <label
                className={[styles.label, disabled ? styles.disabled : null].join(' ')}
                data-testid={props['data-testid'] ? props['data-testid'] + '.label' : null}
                id={name}
            >
                {label}
                <input
                    ref={inputRef}
                    type="checkbox"
                    className={styles.checkbox}
                    name={name || label}
                    onChange={handleClick}
                    checked={Boolean(isChecked)}
                    disabled={Boolean(disabled)}
                    aria-hidden={true}
                    tabIndex={-1}
                    role="mixed"
                    data-testid={props['data-testid'] ? props['data-testid'] + '.hidden' : null}
                />
            </label>
        </div>
    );
};

Checkbox.propTypes = {
    label: PropTypes.string.isRequired,
    name: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    checked: PropTypes.bool,
    disabled: PropTypes.bool,
    indeterminate: PropTypes.bool,
    children: PropTypes.any,
    'data-testid': PropTypes.string
};

Checkbox.defaultProps = {
    label: 'label',
    onChange: () => {},
    checked: false,
    disabled: false,
    indeterminate: false
};

export default memo(Checkbox);
